import * as taskActions from "@rentiohq/shared-frontend/dist/reduxV2/task/task.actions";
import * as taskSelectors from "@rentiohq/shared-frontend/dist/reduxV2/task/task.selectors";
import { IComment } from "@rentiohq/shared-frontend/dist/types/comment.types";
import {
  Button,
  EmptyState,
  ResourceList,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import * as t from "../../services/translationService";
import { CommentListItem } from "./components/CommentListItem";
import CommentSendBar from "./components/CommentSendBar";
import * as S from "./InlineComments.styled";

const INLINE_FETCH_LIMIT = 7;

interface IProps {
  taskId: number;
}

const InlineComments = (props: IProps) => {
  const { taskId } = props;

  // Redux
  const dispatch = useDispatch();

  const isFetchingComments = useSelector((state: IRootStore) =>
    taskSelectors.isFetchingComments(state, taskId),
  );

  const comments = useSelector((state: IRootStore) =>
    taskSelectors.getTaskComments(state, taskId),
  );

  const showLimit = useSelector((state: IRootStore) =>
    taskSelectors.getShowLimit(state),
  );

  // Lifecycle
  React.useEffect(() => {
    fetchData(true);
  }, [taskId]);

  // Data
  const fetchData = (refetch: boolean) => {
    dispatch(
      taskActions.getTaskCommentsStart.getAction({
        taskId,
        refetch,
        limit: INLINE_FETCH_LIMIT,
      }),
    );
  };

  // Event handlers
  const handleClickDelete = (comment: IComment) => {
    dispatch(
      taskActions.commentSetShowLimitStart.getAction({
        taskId,
        showLimit: showLimit > 5 ? showLimit - 1 : showLimit,
      }),
    );
    dispatch(
      taskActions.deleteTaskCommentStart.getAction({
        commentId: comment.id,
        taskId,
      }),
    );
  };

  const handlePressShowMore = () => {
    dispatch(
      taskActions.commentSetShowLimitStart.getAction({
        taskId,
        showLimit: showLimit + 3,
      }),
    );
    fetchData(false);
  };

  // Render
  const renderContent = () => {
    if (comments && comments.length === 0) {
      return (
        <>
          <EmptyState
            backgroundColor="transparent"
            heading={t.commentEmptyTitle()}
            isLoading={isFetchingComments}
          />
        </>
      );
    }
    if (comments && comments.length > 0) {
      const commentsToRender = comments.slice(0, showLimit);
      return (
        <>
          {
            <ResourceList
              items={commentsToRender}
              renderItem={renderComments}
              hasSeparator={true}
            />
          }
          {renderShowAllButton()}
        </>
      );
    }
    return null;
  };

  const renderShowAllButton = () => {
    if (comments && comments.length <= showLimit) {
      return;
    }

    return (
      <S.ShowAllButtonWrap>
        <Button appearance="link" onClick={handlePressShowMore}>
          {t.tasksInlineShowMore()}
        </Button>
      </S.ShowAllButtonWrap>
    );
  };

  const renderComments = (comment: IComment) => {
    return (
      <React.Fragment key={comment.id}>
        <CommentListItem
          comment={comment}
          handleClickDelete={() => handleClickDelete(comment)}
        />
      </React.Fragment>
    );
  };

  return (
    <>
      <CommentSendBar taskId={taskId} />
      {renderContent()}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default InlineComments;
